import React, {useState} from 'react';
import AppleButton from '@jobseeker/lotus/elements/AppleButton';
import GlobalLoaderBackdrop from './GlobalLoaderBackdrop';
import {deferTask, openUrl} from '@jobseeker/common-core/utils';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

type Props = {
  onAccountNotFound?: () => void;
  onError?: (message: string) => void;
};

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME || '';
const isAppHirist = PLATFORM === 'hirist';

const AppleLogin: React.FC<Props> = ({onAccountNotFound, onError}) => {
  const [isLoading, setIsLoading] = useState(false);
  const isAboveMobile = useIsLargeDevice('sm');

  const handleAppleClick = () => {
    deferTask(() => {
      try {
        const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;
        if (!clientId) throw new Error('Apple login not configured');

        const redirectUri = `${window.location.origin}/auth/apple/callback`;
        const state = window.location.pathname + window.location.search;

        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          response_mode: 'form_post',
          scope: 'name email',
          state,
        });

        const url = `https://appleid.apple.com/auth/authorize?${params.toString()}`;

        if (!isAboveMobile) {
          // Mobile: full-page redirect — callback redirects back with apple_error param on failure
          window.location.href = url;
          return;
        }

        let interval: ReturnType<typeof setInterval>;

        const cleanup = () => {
          clearInterval(interval);
          window.removeEventListener('message', onMessage);
        };

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          const {type, nextUrl} = event.data || {};

          if (type === 'APPLE_LOGIN_SUCCESS') {
            cleanup();
            setIsLoading(false);
            openUrl(nextUrl, '_self');
          } else if (type === 'APPLE_LOGIN_FAILURE') {
            cleanup();
            setIsLoading(false);
            if (event.data?.errorCode === 'ACCOUNT_NOT_FOUND') {
              onAccountNotFound?.();
            } else {
              onError?.(event.data?.errorMessage);
            }
          }
        };

        setIsLoading(true);
        window.addEventListener('message', onMessage);
        const popup = window.open(url, 'appleLogin', 'width=500,height=600');

        interval = setInterval(() => {
          if (popup?.closed) {
            cleanup();
            setIsLoading(false);
          }
        }, 500);
      } catch {
        setIsLoading(false);
      }
    });
  };

  return (
    <>
      <AppleButton
        onClick={handleAppleClick}
        disabled={isLoading}
        sx={{
          px: '20px',
          py: '10px',
          lineHeight: '24px',
          width: '100%',
          height: isAppHirist ? '54px' : '40px',
          fontSize: '16px',
          fontWeight: 600,
          borderRadius: isAppHirist ? '8px' : '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: 1.5,
          mt: '12px',
        }}
      />
    </>
  );
};

export default AppleLogin;
